import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { archiveTemplate, getTemplate, scanTemplate } from '@/features/templates/templates-api';
import { useFormatters } from '@/i18n/format';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/templates/$id/')({
  component: TemplateDetailPage,
});

function TemplateDetailPage() {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['templates', id],
    queryFn: () => getTemplate(id),
    retry: false,
  });

  const scan = useMutation({
    mutationFn: () => scanTemplate(id),
    onSuccess: (data) => {
      toast.success(
        t('templates.detail.scanSuccess', { count: data.template.placeholders.length }),
      );
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.setQueryData(['templates', id], data);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('templates.detail.scanFailed');
      toast.error(message);
    },
  });

  const archive = useMutation({
    mutationFn: () => archiveTemplate(id),
    onSuccess: () => {
      toast.success(t('templates.detail.archiveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      void navigate({ to: '/app/templates' });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('templates.detail.archiveFailed');
      toast.error(message);
    },
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('templates.detail.notFoundTitle')}</AlertTitle>
        <AlertDescription>{t('templates.detail.notFoundBody')}</AlertDescription>
      </Alert>
    );
  }

  const template = query.data.template;
  const driveUrl =
    template.kind === 'gdoc'
      ? `https://docs.google.com/document/d/${template.googleFileId}/edit`
      : `https://docs.google.com/spreadsheets/d/${template.googleFileId}/edit`;

  return (
    <div className="space-y-6">
      <Link
        to="/app/templates"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        {t('templates.detail.backLink')}
      </Link>

      <PageHeader
        eyebrow={
          template.kind === 'gdoc' ? t('templates.detail.kindDoc') : t('templates.detail.kindSheet')
        }
        title={template.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="rounded-full">
              {template.category ?? t('templates.detail.withoutCategory')}
            </Badge>
            {template.seedId ? (
              <Badge variant="outline" className="rounded-full">
                {t('templates.detail.fromCatalog', { seedId: template.seedId })}
              </Badge>
            ) : null}
            {template.description ? (
              <span className="block w-full text-sm text-muted-foreground">
                {template.description}
              </span>
            ) : null}
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <a href={driveUrl} target="_blank" rel="noreferrer noopener">
              <Button variant="outline">
                <ExternalLink className="size-4" aria-hidden />
                {t('templates.detail.openInDrive')}
              </Button>
            </a>
            <Link to="/app/templates/$id/mappings" params={{ id: template.id }}>
              <Button variant="outline">{t('templates.detail.mappings')}</Button>
            </Link>
            <Button onClick={() => scan.mutate()} disabled={scan.isPending}>
              {scan.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="size-4" aria-hidden />
              )}
              {t('templates.detail.scanButton')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
            >
              {archive.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-4" aria-hidden />
              )}
              {t('templates.detail.archiveButton')}
            </Button>
          </div>
        }
      />

      <Card className="border-border/70 shadow-soft">
        <CardHeader>
          <CardTitle className="text-base">{t('templates.detail.placeholders.title')}</CardTitle>
          <CardDescription>
            {template.placeholders.length === 0
              ? t('templates.detail.placeholders.descEmpty')
              : t('templates.detail.placeholders.descCount', {
                  count: template.placeholders.length,
                })}
            {template.lastScannedAt
              ? ` · ${t('templates.detail.placeholders.lastScanned', {
                  when: dateTime(template.lastScannedAt),
                })}`
              : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {template.placeholders.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {template.placeholders.map((ph) => (
                <li
                  key={ph}
                  className="rounded-md border border-border/70 bg-muted/40 px-2 py-1 font-mono text-xs text-foreground/90"
                >
                  {`{{${ph}}}`}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('templates.detail.placeholders.empty', {
                defaultValue: 'Run a scan to detect placeholders.',
              })}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
